import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { MdAdd, MdOutlineClear } from "react-icons/md";
import { BsFillFileEarmarkExcelFill } from "react-icons/bs";
import {
  EPaymentTransfer,
  setDialog,
  setSearchBody,
} from "../../../../../app/slices/ePaymentTransferSlice";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import Permission from "../../../../../components/userPermission/userPermission";
import EPaymentTransferForm from "./form";
import UserInfoCard from "../userInfoCard";
import FinancialInfoCard from "../financialInfoCard";
import { restoreFilters } from "../../../../../helpers/restoreFilters";

const formatAmount = (value) =>
  value != null && value !== ""
    ? Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : "-";

const EPaymentTransferTable = ({ posId }) => {
  const dispatch = useDispatch();

  const _EPaymentTransfer = useSelector(
    (state) => state.ePaymentTransfer.ePaymentTransfer,
  );
  const _SearchBody = useSelector((state) => state.ePaymentTransfer.searchBody);
  const _Dialog = useSelector((state) => state.ePaymentTransfer.dialog);
  const _Types = useSelector((state) => state.ePaymentTransfer.types.data);
  const _TypesLoading = useSelector(
    (state) => state.ePaymentTransfer.types.loading,
  );

  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState(() =>
    restoreFilters(
      { transfer_type_id: null, start_date: null, end_date: null },
      _SearchBody,
    ),
  );

  useEffect(() => {
    dispatch(EPaymentTransfer.getTypes());
  }, [dispatch]);

  const columns = [
    {
      field: "sequence",
      headerName: "#",
      flex: 0.5,
    },
    {
      field: "transfer_type_name",
      headerName: "النوع",
      flex: 1.5,
      renderCell: (params) => (
        <Stack>
          <Typography variant="body2">
            {params.row.transfer_type_name}
          </Typography>
          {params.row.note && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {params.row.note}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "amount",
      headerName: "المبلغ (IQD)",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: "bold",
            color: Number(params.row.amount) < 0 ? "error.main" : "inherit",
          }}
        >
          {formatAmount(params.row.amount)}
        </Typography>
      ),
    },
    {
      field: "balance",
      headerName: "الرصيد قبل/بعد العملية (IQD)",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            قبل: {formatAmount(params.row.before_balance)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            بعد: {formatAmount(params.row.after_balance)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) =>
        params.row.created_at ? (
          <Tooltip
            title={dayjs(params.row.created_at).format("YYYY-MM-DD hh:mm a")}
          >
            <Stack>
              <Typography>
                {dayjs(params.row.created_at).format("YYYY-MM-DD")}
              </Typography>
              <Typography variant="caption">
                {dayjs(params.row.created_at).format("hh:mm a")}
              </Typography>
            </Stack>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  // Build the filter body and fetch in a single effect. Splitting these into
  // two effects (one to write searchBody, one to read it back and fetch) caused
  // a duplicate request: the fetch effect ran once with the stale/initial
  // searchBody (no pos_id) before the updated body propagated, and that
  // unfiltered response could overwrite the correct pos-filtered data.
  useEffect(() => {
    const body = {
      pos_id: posId ?? "",
      transfer_type_id: search.transfer_type_id ?? "",
      start_date: search.start_date ?? "",
      end_date: search.end_date ?? "",
      skip: currentPage * pageSize,
      take: pageSize,
    };
    dispatch(setSearchBody(body));
    dispatch(EPaymentTransfer.getAll(body));
  }, [currentPage, dispatch, pageSize, posId, search]);

  return (
    <Card>
      {_Dialog && <EPaymentTransferForm />}
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {/* User Info Card */}
              <Grid item xs={12} md={4}>
                <UserInfoCard />
              </Grid>

              {/* Financial Info Card */}
              <Grid item xs={12} md={4}>
                <FinancialInfoCard />
              </Grid>

              {/* Actions Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "#f57c00", mb: 2 }}
                    >
                      العمليات
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Permission permission="e_payment_transfer_balance">
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            startIcon={<MdAdd />}
                            onClick={() => dispatch(setDialog(true))}
                          >
                            مناقلة رصيد
                          </Button>
                        </Permission>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "flex-start",
                p: 1.5,
                mb: 1,
                borderRadius: 1,
                backgroundColor: "action.hover",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                رصيد الدفع الالكتروني :
              </Typography>
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: "bold" }}
              >
                {(_EPaymentTransfer.total_amount ?? 0).toLocaleString()} (IQD)
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={_Types || []}
              loading={_TypesLoading}
              getOptionLabel={(option) => option.name || ""}
              value={
                _Types?.find((x) => x.id === search.transfer_type_id) ?? null
              }
              onChange={(e, newValue) => {
                setCurrentPage(0);
                setSearch({
                  ...search,
                  transfer_type_id: newValue ? newValue.id : null,
                });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="نوع المناقلة" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateRangepicker
              startDate={search.start_date}
              endDate={search.end_date}
              handleOnChange={(searchDate) => {
                const updatedFilter = {
                  from: dayjs(searchDate[0].startDate).format("YYYY-MM-DD"),
                  to: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                };
                if (
                  updatedFilter.from !== searchDate.startDate ||
                  updatedFilter.to !== searchDate.endDate
                ) {
                  setCurrentPage(0);
                  setSearch({
                    ...search,
                    start_date: updatedFilter.from,
                    end_date: updatedFilter.to,
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Stack direction="row" spacing={0}>
              <Tooltip title="تصدير ملف اكسل">
                <IconButton
                  onClick={() => {
                    dispatch(EPaymentTransfer.exportExcel(_SearchBody));
                  }}
                >
                  <BsFillFileEarmarkExcelFill />
                </IconButton>
              </Tooltip>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch({
                      transfer_type_id: null,
                      start_date: null,
                      end_date: null,
                    });
                    setPageSize(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <ResponsiveDataGrid gridHeight={null}
              rows={_EPaymentTransfer.data.map((x, index) => ({
                ...x,
                sequence: currentPage * pageSize + index + 1,
              }))}
              getRowId={(row) => row.sequence}
              autoHeight
              columns={columns}
              pageSize={pageSize}
              page={currentPage}
              rowCount={_EPaymentTransfer.total}
              loading={_EPaymentTransfer.loading}
              rowHeight={55}
              pagination
              paginationMode="server"
              onPageChange={(newPage) => setCurrentPage(newPage)}
              rowsPerPageOptions={[15, 25, 50, 100]}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              disableSelectionOnClick
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default EPaymentTransferTable;
